import "./FilePageContainer.scss";

import { CreateFileModal } from "components/Modals";
import ArchiveFileModal from "components/Modals/ArchiveFileModal";
import MergeFileModal from "components/Modals/MergeFileModal";
import { KaboodleToaster } from "components/common";
import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { setShowArchiveModal, setShowMergeModal } from "reducers/files";
import { setShowAddModal } from "reducers/main";
import {
  useDeleteFileByIdMutation,
  useMergeFileMutation,
} from "services/file-api";
import { useDispatch, useSelector } from "utils/redux";

type FileContainerPropTypes = {
  header?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

const FilePageContainer = ({
  header,
  footer,
  children,
}: FileContainerPropTypes) => {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const showAddModal = useSelector((state) => state.main.showAddModal);
  const showArchiveModal = useSelector((state) => state.files.showArchiveModal);
  const showMergeModal = useSelector((state) => state.files.showMergeModal);
  const currentFile = useSelector((state) => state.files.currentFile);
  const [deleteFileById] = useDeleteFileByIdMutation();
  const [mergeFile, { isLoading: isMerging }] = useMergeFileMutation();

  const handleConfirmArchive = useCallback(async () => {
    if (!fileId) {
      console.error("No fileId provided for archive operation");
      return;
    }
    try {
      dispatch(setShowArchiveModal(false));
      navigate("/operations/files");
      await deleteFileById({ id: fileId });
      KaboodleToaster.success("File archived successfully");
    } catch (error) {
      console.error("Failed to archive file:", error);
      KaboodleToaster.error("Failed to archive file");
    }
  }, [fileId, deleteFileById, dispatch, navigate]);

  const handleConfirmMerge = useCallback(
    async (targetFileId: number) => {
      if (!fileId) {
        console.error("No fileId provided for merge operation");
        return;
      }
      try {
        const target = await mergeFile({ id: fileId, targetFileId }).unwrap();
        dispatch(setShowMergeModal(false));
        KaboodleToaster.success(
          `File #${fileId} merged into File #${target.id}`,
        );
        // land the user on the file they merged into
        navigate(`/operations/files/${target.id}/edit`);
      } catch (error) {
        // the backend's validate_merge reports every rule under
        // target_file_id; anything else (404, 403) arrives as `detail`
        const detail = (
          error as { data?: { target_file_id?: string[]; detail?: string } }
        )?.data;
        const message =
          detail?.target_file_id?.join(", ") ||
          detail?.detail ||
          "Failed to merge file";
        console.error("Failed to merge file:", error);
        KaboodleToaster.error(message);
      }
    },
    [fileId, mergeFile, dispatch, navigate],
  );

  const modal = useMemo(() => {
    return (
      <CreateFileModal
        isOpen={showAddModal}
        onClose={() => {
          dispatch(setShowAddModal(false));
        }}
      />
    );
  }, [dispatch, showAddModal]);

  const archiveFileModal = useMemo(() => {
    return (
      <ArchiveFileModal
        isOpen={showArchiveModal}
        onClose={() => {
          dispatch(setShowArchiveModal(false));
        }}
        onConfirm={handleConfirmArchive}
      />
    );
  }, [dispatch, showArchiveModal]);

  const mergeFileModal = useMemo(() => {
    return (
      <MergeFileModal
        isOpen={showMergeModal}
        currentFile={currentFile}
        isMerging={isMerging}
        onClose={() => {
          dispatch(setShowMergeModal(false));
        }}
        onConfirm={handleConfirmMerge}
      />
    );
  }, [dispatch, showMergeModal, currentFile, isMerging, handleConfirmMerge]);

  return (
    <>
      {modal}
      {archiveFileModal}
      {mergeFileModal}
      <div className="FilePageContainer">
        {header && <div className="FilePageContainer__Header">{header}</div>}
        <div className="FilePageContainer__Forms">{children}</div>
        {footer && <div className="FilePageContainer__Footer">{footer}</div>}
      </div>
    </>
  );
};

export default FilePageContainer;
